import { useMemo, useState } from 'react';

import { ApiClient, formatMoney, formatRelative, useAsync, useDebounced } from '@clientma/core';
import { Alert, Badge, Button, Drawer, Input, Select, Skeleton } from '@clientma/ui';

import { AccountActions } from './AccountActions';
import { providerApi } from '../api';

/**
 * Every organisation SJS Tech sells to.
 *
 * The columns are the ones that start a conversation: what they pay, what they owe, and when
 * their trial runs out. Seat and user counts are here too because "you are paying for twelve and
 * using four" is the other conversation this list exists to make possible.
 */
export default function Tenants({ api }: { api: ApiClient }) {
  const provider = useMemo(() => providerApi(api), [api]);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);

  const debounced = useDebounced(search, 300);

  const { data, isLoading, error, reload } = useAsync(
    (signal) =>
      provider.tenants(
        { search: debounced || undefined, status: status || undefined, page, pageSize: 25 },
        signal,
      ),
    [provider, debounced, status, page],
  );

  return (
    <div className="cma-stack">
      <div className="provider-toolbar">
        <Input
          value={search}
          placeholder="Search by name or URL…"
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
        />

        <Select
          value={status}
          aria-label="Status"
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(1);
          }}
        >
          <option value="">Any status</option>
          <option value="Trial">Trial</option>
          <option value="Active">Active</option>
          <option value="Suspended">Suspended</option>
          <option value="Cancelled">Cancelled</option>
        </Select>
      </div>

      {error && (
        <Alert tone="danger" title="The customer list could not be loaded">
          {error.message}
        </Alert>
      )}

      {isLoading ? (
        <Skeleton height={280} />
      ) : (data?.items ?? []).length === 0 ? (
        <Alert tone="info">No organisation matches that.</Alert>
      ) : (
        <div className="provider-table-wrap">
          <table className="provider-table">
            <thead>
              <tr>
                <th>Organisation</th>
                <th>Status</th>
                <th>Plan</th>
                <th className="provider-num">Seats</th>
                <th className="provider-num">Owing</th>
                <th>Trial ends</th>
              </tr>
            </thead>

            <tbody>
              {(data?.items ?? []).map((tenant) => (
                <tr key={tenant.id} onClick={() => setOpenId(tenant.id)} tabIndex={0}>
                  <td>
                    <div className="provider-strong">{tenant.name}</div>
                    <div className="provider-muted">
                      {tenant.slug} · {tenant.countryCode}
                    </div>
                  </td>

                  <td>
                    <StatusBadge status={tenant.status} />
                  </td>

                  <td>
                    <div>{tenant.planName ?? '—'}</div>
                    {tenant.subscriptionStatus && (
                      <div className="provider-muted">{tenant.subscriptionStatus}</div>
                    )}
                  </td>

                  <td className="provider-num">
                    {tenant.usersActive} / {tenant.seats}
                  </td>

                  <td className="provider-num">
                    {tenant.outstandingAmount > 0 ? (
                      <span className="provider-owing">
                        {formatMoney(tenant.outstandingAmount, tenant.currency)}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>

                  <td>
                    {tenant.trialEndsAtUtc ? formatRelative(tenant.trialEndsAtUtc) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.totalPages > 1 && (
        <div className="provider-actionrow">
          <span className="provider-muted">
            Page {data.page} of {data.totalPages} · {data.totalCount} organisations
          </span>

          <div className="cma-row" style={{ gap: 8 }}>
            <Button
              size="sm"
              variant="ghost"
              disabled={!data.hasPrevious}
              onClick={() => setPage((current) => current - 1)}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={!data.hasNext}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {openId && (
        <TenantDrawer
          api={api}
          tenantId={openId}
          onClose={() => setOpenId(null)}
          onChanged={reload}
        />
      )}
    </div>
  );
}

function TenantDrawer({
  api,
  tenantId,
  onClose,
  onChanged,
}: {
  api: ApiClient;
  tenantId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const provider = useMemo(() => providerApi(api), [api]);

  const { data, isLoading, reload } = useAsync(
    (signal) => provider.tenant(tenantId, signal),
    [provider, tenantId],
  );

  const [suspending, setSuspending] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changeStatus = async (status: string) => {
    setBusy(true);
    setError(null);

    try {
      await provider.setTenantStatus(tenantId, status, reason);
      setSuspending(false);
      setReason('');
      reload();
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer open title={data?.summary.name ?? 'Customer'} onClose={onClose}>
      {isLoading || !data ? (
        <Skeleton height={320} />
      ) : (
        <div className="cma-stack">
          {error && <Alert tone="danger">{error}</Alert>}

          {data.isReadOnly && (
            <Alert tone="warning" title="Read-only">
              This organisation is suspended. It can be read and exported but not written to.
            </Alert>
          )}

          <div className="cma-row" style={{ gap: 6, flexWrap: 'wrap' }}>
            <StatusBadge status={data.summary.status} />
            <Badge tone="neutral">{data.summary.countryCode}</Badge>
            <Badge tone="neutral">{data.summary.currency}</Badge>
            {data.allowSupportImpersonation && <Badge tone="info">Support access allowed</Badge>}
          </div>

          <Section title="Billing contact" />
          <Detail label="Name" value={data.billingContactName} />
          <Detail label="Email" value={data.billingEmail} />
          <Detail label="Tax number" value={data.billingTaxNumber} />

          {data.lines.length > 0 && (
            <>
              <Section title="What they pay for" />
              {data.lines.map((line, index) => (
                <div key={`${line.description}-${index}`} className="provider-linerow">
                  <span>
                    {line.description}
                    {line.quantity > 1 && <span className="provider-muted"> × {line.quantity}</span>}
                  </span>
                  <span className="provider-num">
                    {formatMoney(line.baseAmount, line.currency)}
                  </span>
                </div>
              ))}
            </>
          )}

          {data.recentInvoices.length > 0 && (
            <>
              <Section title="Recent invoices" />
              {data.recentInvoices.map((invoice) => (
                <div key={invoice.id} className="provider-linerow">
                  <span>
                    {invoice.invoiceNumber}
                    <span className="provider-muted"> · {invoice.status}</span>
                    {invoice.declaredPayments > 0 && (
                      <Badge tone="warning">{invoice.declaredPayments} declared</Badge>
                    )}
                  </span>
                  <span className={`provider-num${invoice.isOverdue ? ' provider-owing' : ''}`}>
                    {formatMoney(invoice.amountDue, invoice.currency)}
                  </span>
                </div>
              ))}
            </>
          )}

          <Section title="The account" />

          {/*
            Changing the plan, raising an invoice, telling them about it and writing down what
            was agreed are one job rather than four screens — somebody finishing a call does all
            of them, and splitting them up means typing the same context four times.
          */}
          <AccountActions
            api={api}
            tenantId={tenantId}
            detail={data}
            onChanged={() => {
              reload();
              onChanged();
            }}
          />

          <Section title="Status" />

          {suspending ? (
            <div className="cma-stack cma-stack--tight">
              <Input
                value={reason}
                placeholder="Why is it being suspended?"
                onChange={(event) => setReason(event.target.value)}
              />

              <div className="cma-row" style={{ gap: 8 }}>
                <Button
                  variant="danger"
                  isLoading={busy}
                  disabled={!reason.trim()}
                  onClick={() => void changeStatus('Suspended')}
                >
                  Suspend
                </Button>
                <Button variant="ghost" onClick={() => setSuspending(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="cma-row" style={{ gap: 8 }}>
              {data.summary.status === 'Suspended' ? (
                <Button isLoading={busy} onClick={() => void changeStatus('Active')}>
                  Restore access
                </Button>
              ) : (
                <Button variant="ghost" onClick={() => setSuspending(true)}>
                  Suspend
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === 'Active'
      ? 'success'
      : status === 'Suspended' || status === 'Cancelled'
        ? 'danger'
        : 'warning';

  return <Badge tone={tone}>{status}</Badge>;
}

function Section({ title }: { title: string }) {
  return <div className="provider-section">{title}</div>;
}

function Detail({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="provider-linerow">
      <span className="provider-muted">{label}</span>
      <span>{value ?? '—'}</span>
    </div>
  );
}
